import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FiCheck, FiChevronDown, FiImage, FiVideo, FiFileText, FiRefreshCw, FiX } from 'react-icons/fi';

export function shortPostText(value, limit = 10) {
  const words = String(value || '').trim().split(/\s+/).filter(Boolean);
  return words.slice(0, limit).join(' ') + (words.length > limit ? '...' : '');
}

export function PlatformPicker({ platforms, accounts, selectedPlatforms, selectedKeys, togglePlatform, toggleAccount, Logo }) {
  const [expanded, setExpanded] = useState(null);
  const strip = useRef(null);
  const platform = platforms.find((item) => item.id === expanded);
  const visibleAccounts = accounts.filter((item) => item.platformId === expanded);
  useEffect(() => {
    const element = strip.current;
    if (!element) return undefined;
    const scroll = (event) => {
      if (!event.deltaY || element.scrollWidth <= element.clientWidth) return;
      const canMove = event.deltaY > 0 ? element.scrollLeft < element.scrollWidth - element.clientWidth - 1 : element.scrollLeft > 0;
      if (canMove) { event.preventDefault(); element.scrollBy({ left: event.deltaY, behavior: 'smooth' }); }
    };
    element.addEventListener('wheel', scroll, { passive: false });
    return () => element.removeEventListener('wheel', scroll);
  }, [expanded, visibleAccounts.length]);
  return <div className="publishing-picker">
    <div className="publishing-platforms" aria-label="Publishing destinations">
      {platforms.map((item) => {
        const selected = item.connected && selectedPlatforms.includes(item.id) && accounts.some((account) => account.platformId === item.id && selectedKeys.includes(account.key));
        const open = () => { if (item.connected && !selectedPlatforms.includes(item.id)) togglePlatform(item.id); setExpanded(expanded === item.id ? null : item.id); };
        return <div className="publishing-platform" key={item.id} data-selected={selected} style={{ '--brand': item.color }}>
          <button type="button" className="publishing-logo" aria-label={`${item.label} accounts`} aria-expanded={expanded === item.id} title={item.label} onClick={open}><Logo id={item.id} label={item.label} /></button>
          <button type="button" className="publishing-chevron" aria-label={`Choose ${item.label} pages`} title={`Choose ${item.label} pages`} onClick={open}><FiChevronDown /></button>
          {selected && <button className="publishing-check" type="button" aria-label={`Deselect ${item.label}`} title={`Deselect ${item.label}`} onClick={() => togglePlatform(item.id)}><FiCheck /></button>}
        </div>;
      })}
    </div>
    {platform && <div className="publishing-account-panel compact">
      {visibleAccounts.length > 0
        ? <div ref={strip} className="publishing-account-strip" aria-label={`${platform.label} pages`}>
          {visibleAccounts.map((account) => <button type="button" key={account.key} aria-label={account.label} aria-pressed={selectedKeys.includes(account.key)} className="publishing-account" onClick={() => toggleAccount(account.key)} title={account.label}>
            <span className="publishing-account-avatar">{account.avatarUrl ? <img src={account.avatarUrl} alt="" onError={(event) => { event.currentTarget.style.display = 'none'; }} /> : null}<b>{account.label.replace(/^@/, '').slice(0, 2).toUpperCase()}</b></span>
            <span>{account.label}</span><FiCheck className="account-check" />
          </button>)}
        </div>
        : <small className="publishing-account-empty">{platform.comingSoon ? 'Coming soon' : 'Not connected'}</small>}
      <button type="button" className="publishing-account-close" aria-label="Close account selection" title="Close" onClick={() => setExpanded(null)}><FiX /></button>
    </div>}
  </div>;
}

export function PostThumbnail({ post, workspaceId, apiBase }) {
  const platform = String(post.platform || post.platforms?.[0] || '').toLowerCase();
  const accountId = String(post.accountId || post.pageId || '');
  const providerPostId = String(post.providerPostId || post.externalPostId || post.id || '').split(',')[0].trim();
  const providerProxy = ['facebook', 'instagram'].includes(platform) && accountId && /^\d+(?:_\d+)?$/.test(providerPostId)
    ? `${apiBase}/api/analytics/post-thumbnail?platform=${encodeURIComponent(platform)}&accountId=${encodeURIComponent(accountId)}&postId=${encodeURIComponent(providerPostId)}&workspaceId=${encodeURIComponent(workspaceId)}`
    : '';
  const initial = post.thumbnailUrl || post.thumbnail_url || (post.mediaType === 'image' ? post.mediaUrl : '') || providerProxy;
  const [src, setSrc] = useState(initial);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const element = useRef(null);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setSrc(initial); setFailed(false);
    if ((initial && !retry) || !post.externalPostId || !/^[a-f\d]{24}$/i.test(String(post.id))) return () => { active = false; };
    let requested = false;
    const load = () => {
      if (requested) return;
      requested = true;
      axios.get(`${apiBase}/api/posts/${encodeURIComponent(post.id)}/media`, { withCredentials: true, headers: { 'x-workspace-id': workspaceId }, signal: controller.signal })
        .then(({ data }) => { if (active) { setSrc(data.thumbnailUrl || ''); setFailed(!data.thumbnailUrl && post.mediaType !== 'text'); } })
        .catch(() => { if (active) setFailed(true); });
    };
    const observer = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) load(); }, { rootMargin: '100px' }) : null;
    if (observer && element.current) observer.observe(element.current); else load();
    return () => { active = false; controller.abort(); observer?.disconnect(); };
  }, [initial, post.id, post.externalPostId, post.mediaType, workspaceId, apiBase, retry]);
  return <div className="managed-thumbnail post-thumbnail" ref={element}>
    {src && !failed ? <img src={src} alt={`${post.pageName || 'Post'} preview`} loading="lazy" onError={() => {
      if (providerProxy && src !== providerProxy) setSrc(providerProxy);
      else if (!retry && post.externalPostId && /^[a-f\d]{24}$/i.test(String(post.id))) setRetry(1);
      else setFailed(true);
    }} /> : <div className="managed-media-fallback">{['video', 'reel', 'short'].includes(post.mediaType) ? <FiVideo /> : post.mediaType === 'image' ? <FiImage /> : <FiFileText />}{failed && <><small>Preview unavailable</small><button type="button" aria-label="Retry thumbnail" title="Retry thumbnail" onClick={() => setRetry((value) => value + 1)}><FiRefreshCw /></button></>}</div>}
  </div>;
}
